"use client";

import DOMPurify from "dompurify";
import React, { type FormEvent, useCallback, useState } from "react";
import * as z from "zod";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address."),
  firstName: z.string().min(1, "Please enter your first name."),
  lastName: z.string().min(1, "Please enter your last name."),
  message: z.string().min(1, "Please enter a brief message.").optional(), // message is now optional
});

type FormData = z.infer<typeof formSchema>;

export default function ContactForm(): React.JSX.Element {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    firstName: "",
    lastName: "",
    message: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false); // New state to track form submission
  const [serverError, setServerError] = useState<string | null>(null); // New state for server error

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [],
  );

  const validateForm = useCallback(() => {
    try {
      formSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof FormData, string>> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as keyof FormData] = err.message;
          }
        });

        setErrors(newErrors);

        // Focus on the first invalid field
        const firstErrorField = Object.keys(newErrors)[0];
        if (firstErrorField) {
          document.getElementById(firstErrorField)?.focus();
        }
      }
      return false;
    }
  }, [formData]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!validateForm()) return;

      setIsSubmitting(true);
      setIsFormSubmitted(false); // Reset success message on new submission

      try {
        // Sanitize form field values using formData state
        const sanitizedEmail = DOMPurify.sanitize(formData.email);
        const sanitizedFirstName = DOMPurify.sanitize(formData.firstName);
        const sanitizedLastName = DOMPurify.sanitize(formData.lastName);
        const sanitizedMessage = DOMPurify.sanitize(formData.message || "");

        // Now, you can send the sanitized data to your API route
        const sanitizedFormData = {
          email: sanitizedEmail,
          firstName: sanitizedFirstName,
          lastName: sanitizedLastName,
          message: sanitizedMessage,
        };

        const response = await fetch("/api/contact", {
          body: JSON.stringify(sanitizedFormData),
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
        });

        if (!response.ok) {
          // Check if the response status is 429 for rate limiting
          if (response.status === 429) {
            setServerError("Too many requests. Please try again later.");
          } else {
            setServerError("Failed to submit form. Please try again.");
          }
          throw new Error("Failed to submit form");
        }

        console.log("Form submitted successfully");

        setFormData({
          email: "",
          firstName: "",
          lastName: "",
          message: "",
        });

        setIsFormSubmitted(true); // Show success message
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm],
  );

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    void handleSubmit(event);
  };

  return (
    <form className="contact-form" onSubmit={onSubmit}>
      <div className="contact-form-cell">
        <label htmlFor="firstName">
          <em>First Name*</em>
        </label>
        <input
          aria-invalid={!!errors.firstName}
          autoComplete="given-name"
          id="firstName"
          name="firstName"
          type="text"
          value={formData.firstName}
          onChange={handleInputChange}
        />
        {errors.firstName && <span role="alert">{errors.firstName}</span>}
      </div>

      <div className="contact-form-cell">
        <label htmlFor="lastName">
          <em>Last Name*</em>
        </label>
        <input
          aria-invalid={!!errors.lastName}
          autoComplete="family-name"
          id="lastName"
          name="lastName"
          type="text"
          value={formData.lastName}
          onChange={handleInputChange}
        />
        {errors.lastName && <span role="alert">{errors.lastName}</span>}
      </div>

      <div className="contact-form-cell contact-form-cell-email">
        <label htmlFor="email">
          <em>Email*</em>
        </label>
        <input
          aria-invalid={!!errors.email}
          autoComplete="email"
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
        />
        {errors.email && <span role="alert">{errors.email}</span>}
      </div>

      <div className="contact-form-cell contact-form-cell-message">
        <label htmlFor="message">
          <em>Message</em>
        </label>
        <textarea
          autoComplete="off"
          id="message"
          maxLength={1000}
          name="message"
          spellCheck="true"
          value={formData.message}
          onChange={handleInputChange}
        />
      </div>

      <div className="contact-form-cell contact-form-submit">
        <div className="form-submit-with-success-message">
          <div className="form-submit-with-success-message-cell">
            <button
              className="contact-form-submit-button"
              data-is-submitting={isSubmitting ? "true" : "false"}
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? "Sending" : "Send"}
            </button>
          </div>
          {isFormSubmitted && ( // Render success message conditionally
            <div className="form-submit-with-success-message-cell">
              Thank you. I&#8217;ll respond to your message as soon as possible.
            </div>
          )}
          {serverError && ( // Conditionally render the error message
            <div className="form-submit-with-error-message-cell">{serverError}</div>
          )}
        </div>
      </div>
    </form>
  );
}
